/**
 * Texts of the clans of the players (item 8: the clans tab, the card of a clan, the dialogs, the history). As
 * everywhere in the game, the Russian text is the source and the translation key (src/game/runtime/i18n.ts); the
 * translations are in tools/i18n/extra.json. A text with a player's or a clan's name, or a number, in it is a template
 * with {placeholders}: the template is translated as a whole (so every language puts the name where its grammar wants
 * it) and the values are put in afterwards (`compose`): names are data and are never translated. Such composed text is
 * DISPLAY text and goes into its field with setRawText (src/game/ui/controls.ts).
 * The history of a clan is structured on the server (ClanEvent); the client writes its lines (`eventText`).
 */
import { translate } from './i18n';

// ---------------------------------------------------------------- the tab, the screen
export const MSG_CLANS = 'Кланы';
export const MSG_CLANS_HINT = 'Ваш клан: игроки, территории и история. Создайте клан или вступите в существующий.';
export const MSG_CLAN = 'Клан';
export const MSG_CLANS_UNAVAILABLE = 'Кланы на этом сервере недоступны.';
export const MSG_NO_CLAN_INTRO = 'Вы не в клане. Члены клана не нападают на территории друг друга и получают 10% дани с территорий соклановцев. Вступите в клан или создайте свой.';
/** The facts of a clan under its name: the numbers in bold (see factsText). */
export const TPL_FACTS = 'Рейтинг {rating} · игроков {members}/{max} · территорий {zones} · {policy}';
/** The facts without the join policy (the clans tab shows it right of the name). */
export const TPL_FACTS_SHORT = 'Рейтинг {rating} · игроков {members}/{max} · территорий {zones}';
export const MSG_POLICY_OPEN = 'вступление открытое';
export const MSG_POLICY_REQUEST = 'вступление по заявке';

// ---------------------------------------------------------------- the sections of the list
export const TPL_SECTION_PLAYERS = 'Игроки ({n}/{max})';
export const TPL_SECTION_ZONES = 'Территории ({n})';
export const MSG_SECTION_HISTORY = 'История';
export const TPL_SECTION_REQUESTS = 'Заявки на вступление ({n})';
export const MSG_SECTION_MY_REQUESTS = 'Ваши заявки';
export const MSG_SECTION_BY_RATING = 'Кланы по рейтингу';
export const TPL_SECTION_SEARCH = 'Поиск: «{query}»';
export const MSG_LOADING = 'Загрузка…';
export const MSG_LOAD_FAILED = 'Не удалось загрузить. Попробуйте ещё раз.';
export const MSG_NO_ZONES = 'Территорий пока нет.';
export const MSG_NO_HISTORY = 'Событий пока нет.';
export const MSG_NO_REQUESTS = 'Новых заявок нет.';
export const MSG_NO_CLANS = 'Кланов пока нет. Создайте первый!';
export const MSG_NOTHING_FOUND = 'Ничего не найдено.';
export const MSG_MORE = 'Показать ещё…';
export const MSG_MORE_CLANS = 'Показать ещё кланы…';

// ---------------------------------------------------------------- rows
export const MSG_ROLE_LEADER = 'лидер';
export const MSG_ROLE_OFFICER = 'офицер';
export const MSG_ROLE_MEMBER = 'участник';
/** A member: the name in bold. */
export const TPL_MEMBER = '{name} — {role} · ур. {level} · захватов: {captures}';
export const TPL_YOU = 'Вы ({name})';
/** A zone of the clan: "№5 — Ann · этап 12 · 5 000 зол./час". */
export const TPL_ZONE = '№{zone} — {owner} · этап {stage} · {gold} зол./час';
/** A pending request of another player (leader and officers). */
export const TPL_REQUEST = '{name} — ур. {level} · {when}';
/** A pending request of the player (no clan yet). */
export const TPL_MY_REQUEST = 'Заявка в клан «{clan}» ждёт решения';
/** A clan of the list: its name in bold, then its members and join policy in grey. */
export const TPL_CLAN_ROW = '{name} {members}/{max} · {policy}';
export const MSG_POLICY_OPEN_SHORT = 'открытый';
export const MSG_POLICY_REQUEST_SHORT = 'по заявке';
export const MSG_TODAY = 'сегодня';
export const MSG_YESTERDAY = 'вчера';

// ---------------------------------------------------------------- the actions
export const TPL_SELECTED = 'Выбрано: {name}';
export const MSG_THIS_IS_YOU = 'Это вы.';
export const MSG_KICK = 'Выгнать';
export const MSG_PROMOTE = 'Повысить';
export const MSG_DEMOTE = 'Понизить';
export const MSG_TRANSFER = 'Лидерство';
export const MSG_SETTINGS = 'Настройки';
export const MSG_ACCEPT = 'Принять';
export const MSG_DECLINE = 'Отклонить';
export const MSG_CANCEL_REQUEST = 'Отменить';
export const MSG_LEAVE = 'Покинуть клан';
export const TPL_REQUESTS_BUTTON = 'Заявки ({n})';
export const MSG_BACK_TO_CLAN = 'К клану';
export const MSG_CREATE_CLAN = 'Создать клан';
export const MSG_FIND = 'Найти';
export const MSG_SEARCH_LABEL = 'Поиск по названию:';
export const TPL_CREATE_RULES = 'Создание: с {level} уровня, один клан на игрока.';
export const MSG_STAFF_HINT = 'Выберите игрока в списке, чтобы управлять им.';
export const MSG_MEMBER_HINT = 'Выберите территорию, чтобы открыть её на карте.';
export const MSG_REQUESTS_HINT = 'Выберите заявку, чтобы принять или отклонить её.';

// ---------------------------------------------------------------- the card of a clan
export const MSG_JOIN = 'Вступить';
export const MSG_SEND_REQUEST = 'Подать заявку';
export const MSG_WITHDRAW_REQUEST = 'Отменить заявку';
export const MSG_YOUR_CLAN = 'Это ваш клан.';
export const TPL_IN_OTHER_CLAN = 'Вы уже в клане «{clan}».';
export const MSG_REQUEST_SENT = 'Заявка подана. Решение примет лидер или офицер клана.';
export const MSG_CLAN_NOT_FOUND = 'Клан не найден.';

// ---------------------------------------------------------------- the dialog of a new clan / the settings
export const MSG_NEW_CLAN = 'Новый клан';
export const MSG_CLAN_SETTINGS = 'Настройки клана';
export const TPL_NAME_LABEL = 'Название ({min}–{max}):';
export const MSG_EMBLEM_LABEL = 'Герб — выберите один:';
export const MSG_OPEN_JOIN = 'Вступление без заявки (открытый клан)';
export const TPL_DESCRIPTION_LABEL = 'Описание (до {max} символов, необязательно):';
export const MSG_CREATE_RULES_LONG = 'Название уникально (без учёта регистра и похожих букв). Основатель становится лидером. Лидер может покинуть клан, только передав лидерство или распустив клан.';
export const MSG_RENAME_RULE = 'Переименовать клан можно раз в 7 дней.';
export const MSG_OFFICER_EDIT = 'Офицер может менять только описание клана.';
export const MSG_SAVE = 'Сохранить';
export const MSG_DISBAND = 'Распустить клан';
export const MSG_SAVED = 'Настройки клана сохранены.';

// ---------------------------------------------------------------- the questions
export const MSG_Q_LEAVE = 'Покинуть клан?';
export const TPL_Q_LEAVE_TEXT = 'Вы выйдете из клана «{clan}», ваши территории перестанут быть территориями клана. Вступить в клан снова можно будет через сутки.';
export const TPL_Q_LEAVE_LAST = 'Вы единственный участник клана «{clan}»: клан будет распущен.';
export const MSG_Q_KICK = 'Выгнать из клана?';
export const TPL_Q_KICK_TEXT = '{name} покинет клан вместе со своими территориями.';
export const MSG_Q_TRANSFER = 'Передать лидерство?';
export const TPL_Q_TRANSFER_TEXT = '{name} станет лидером клана, а вы — офицером.';
export const MSG_Q_DISBAND = 'Распустить клан?';
export const MSG_Q_DISBAND_TEXT = 'Все участники покинут клан, его история будет удалена. Чтобы подтвердить, введите название клана:';

// ---------------------------------------------------------------- what happened (notices)
export const TPL_N_ACCEPTED = 'Вас приняли в клан «{clan}».';
export const TPL_N_JOINED = 'Вы вступили в клан «{clan}».';
export const TPL_N_CREATED = 'Клан «{clan}» создан. Вы его лидер.';
export const MSG_N_KICKED = 'Вас исключили из клана.';
export const MSG_N_LEFT = 'Вы покинули клан.';
export const MSG_N_DISBANDED = 'Клан распущен.';
export const MSG_N_OFFICER = 'Вы теперь офицер клана.';
export const MSG_N_MEMBER = 'Вы теперь участник клана.';
export const MSG_N_LEADER = 'Вы теперь лидер клана.';
export const MSG_N_REQUESTS = 'Новая заявка на вступление в клан.';

// ---------------------------------------------------------------- refusals of another's state
/** clan.decide: the requester is in a clan by now (details.playerId: not the deciding player's own clan). */
export const MSG_PLAYER_IN_CLAN = 'Игрок уже вступил в другой клан.';
/** clan_rank_too_low with details.need 'leader' (an officer kicks an officer, ...). */
export const MSG_LEADER_ONLY = 'Это может сделать только лидер клана.';

// ---------------------------------------------------------------- refusals (details.reason of the server)
export const MSG_FAILED = 'Не удалось выполнить действие. Попробуйте ещё раз.';
export const TPL_RETRY_IN = '{what} Можно будет через {time}.';
export const TPL_HOURS_MINUTES = '{h} ч {m} мин';
export const TPL_MINUTES = '{m} мин';
export const TPL_DAYS_HOURS = '{d} дн. {h} ч';
export const REASON_TEXT: Record<string, string> = {
  clan_name_required: 'Введите название клана.',
  clan_name_too_short: 'Название слишком короткое (минимум 3 символа).',
  clan_name_too_long: 'Название слишком длинное (максимум 20 символов).',
  clan_name_invalid: "Только буквы одного алфавита, цифры, пробелы, - . и '; нужна хотя бы одна буква.",
  clan_name_denied: 'Такое название использовать нельзя.',
  clan_name_taken: 'Клан с таким названием уже есть.',
  emblem_invalid: 'Выберите герб из списка.',
  description_too_long: 'Описание слишком длинное (максимум 120 символов).',
  description_invalid: 'В описании есть недопустимые символы.',
  description_denied: 'Такое описание использовать нельзя.',
  guest_not_allowed: 'Гость не может создать клан: зарегистрируйтесь (ник и пароль).',
  clan_level_too_low: 'Создать клан можно с 5 уровня.',
  create_cooldown: 'Новый клан можно создавать раз в сутки.',
  rename_cooldown: 'Переименовать клан можно раз в 7 дней.',
  rejoin_cooldown: 'После выхода из клана вступить снова можно через сутки.',
  already_in_clan: 'Вы уже состоите в клане.',
  clan_full: 'В клане нет свободных мест.',
  request_limit: 'У вас уже 3 заявки: дождитесь ответа или отмените одну.',
  clan_requests_full: 'У клана слишком много заявок. Попробуйте позже.',
  clan_open: 'Этот клан открыт: в него можно вступить сразу.',
  clan_request_required: 'В этот клан вступают по заявке.',
  officer_limit: 'В клане уже 5 офицеров.',
  leader_must_transfer: 'Сначала передайте лидерство другому игроку.',
  disband_confirm_mismatch: 'Название не совпадает с названием клана.',
  clan_not_found: 'Клан не найден.',
  request_not_found: 'Эта заявка уже рассмотрена.',
  not_a_member: 'Игрок уже не в клане.',
  target_is_leader: 'Лидера нельзя выгнать.',
  cannot_target_self: 'Это действие нельзя применить к себе.',
  clan_rank_too_low: 'Для этого нужны права лидера или офицера.',
};

// ---------------------------------------------------------------- the history (ClanEvent -> a line)
export const TPL_EV_CREATED = '{actor} — основатель клана';
export const TPL_EV_RENAMED = 'Клан переименован: «{from}» → «{to}» ({actor})';
export const TPL_EV_SETTINGS = 'Изменено: {what} ({actor})';
export const MSG_EV_WHAT_EMBLEM = 'герб';
export const MSG_EV_WHAT_POLICY = 'условия вступления';
export const MSG_EV_WHAT_DESCRIPTION = 'описание';
export const TPL_EV_JOINED = 'Новый участник: {actor}';
export const TPL_EV_JOINED_BY = 'Новый участник: {actor} (по заявке, решение: {by})';
export const TPL_EV_LEFT = 'Выход из клана: {actor}';
export const TPL_EV_LEFT_ZONES = 'Выход из клана: {actor} (вместе с территориями {zones})';
export const TPL_EV_KICKED = 'Исключение из клана: {target} (решение: {actor})';
export const TPL_EV_OFFICER = 'Новый офицер: {target} (назначение: {actor})';
export const TPL_EV_MEMBER = '{target} больше не офицер (решение: {actor})';
export const TPL_EV_LEADER = 'Лидерство передано: {actor} → {target}';
export const TPL_EV_LEADER_AUTO = 'Новый лидер: {target} (аккаунт лидера удалён)';
export const TPL_EV_LEADER_MODERATED = 'Новый лидер: {target} (решение администрации)';
export const TPL_EV_ZONE_CAPTURED = 'Захвачена территория №{zone}: {actor}';
export const TPL_EV_ZONE_LOST = 'Потеряна территория №{zone}; новый владелец: {by}';
export const TPL_EV_ZONE_LOST_CLAN = 'Потеряна территория №{zone}; новый владелец: {by} (клан «{clan}»)';
export const TPL_EV_MEMBER_DELETED = 'Аккаунт участника удалён: {target}';
export const TPL_EV_MOD_RENAME = 'Администрация переименовала клан: «{from}» → «{to}»';
export const MSG_EV_MOD_DESCRIPTION = 'Администрация очистила описание клана';
export const TPL_EV_MOD_REMOVE = 'Администрация исключила из клана: {target}';
export const TPL_EV_MOD_LEADER = 'Администрация назначила лидера: {target}';
export const TPL_ZONE_NUMBER = '№{zone}';

/** A composed line: the text and where each value stands in it (for bold names, grey dates). */
export interface Composed {
  text: string;
  at: Record<string, [number, number]>;
}

/**
 * The template in the active language with its {placeholders} replaced by the values (as they are: names are never
 * translated); `at` says where each value ended up.
 */
export function compose(template: string, values: Record<string, string | number> = {}): Composed {
  const t = translate(template);
  const at: Record<string, [number, number]> = {};
  let text = '';
  let i = 0;
  for (const m of t.matchAll(/\{([a-z]+)\}/g)) {
    const key = m[1];
    if (!(key in values)) continue;
    text += t.slice(i, m.index);
    const v = String(values[key]);
    at[key] = [text.length, text.length + v.length];
    text += v;
    i = m.index! + m[0].length;
  }
  text += t.slice(i);
  return { text, at };
}

/** compose(...).text */
export function fill(template: string, values: Record<string, string | number> = {}): string {
  return compose(template, values).text;
}

/** How long until a cooldown ends, roughly: "5 ч 20 мин", "2 дн. 3 ч" (display text). */
export function durationText(ms: number): string {
  const min = Math.max(1, Math.ceil(ms / 60000));
  if (min < 60) return fill(TPL_MINUTES, { m: min });
  const h = Math.floor(min / 60);
  if (h < 24) return fill(TPL_HOURS_MINUTES, { h, m: min % 60 });
  return fill(TPL_DAYS_HOURS, { d: Math.floor(h / 24), h: h % 24 });
}

/** The text of a refusal of the server (its details.reason), display text. */
export function reasonText(reason: string | undefined | null): string {
  return translate((reason && REASON_TEXT[reason]) || MSG_FAILED);
}

/** "сегодня 14:05", "вчера 22:10", "23.09 10:15" (display text; local time). */
export function dateText(at: number, now: number): string {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  const time = two(d.getHours()) + ':' + two(d.getMinutes());
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const today = day(new Date(now));
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day(d) === today) return translate(MSG_TODAY) + ' ' + time;
  if (day(d) === day(yesterday)) return translate(MSG_YESTERDAY) + ' ' + time;
  return two(d.getDate()) + '.' + two(d.getMonth() + 1) + ' ' + time;
}

/** A clan event as the game reads it (ClanEvent of src/shared/protocol/model.ts). */
export interface EventLike {
  type: string;
  actorName: string | null;
  targetName: string | null;
  zone?: number;
  zones?: number[];
  role?: string;
  from?: string;
  to?: string;
  emblem?: number;
  joinPolicy?: string;
  description?: boolean;
  via?: string;
  byName?: string | null;
  byClanName?: string | null;
  action?: string;
  reason?: string;
}

/** The line of a history event (display text) and where its names stand (they are shown in bold). */
export function eventText(e: EventLike): Composed {
  const actor = e.actorName ?? '?';
  const target = e.targetName ?? '?';
  const zones = (e.zones ?? []).map((z) => fill(TPL_ZONE_NUMBER, { zone: z })).join(', ');
  switch (e.type) {
    case 'created':
      return compose(TPL_EV_CREATED, { actor });
    case 'renamed':
      return compose(TPL_EV_RENAMED, { from: e.from ?? '', to: e.to ?? '', actor });
    case 'settings': {
      const what: string[] = [];
      if (e.emblem !== undefined) what.push(translate(MSG_EV_WHAT_EMBLEM));
      if (e.joinPolicy !== undefined) what.push(translate(MSG_EV_WHAT_POLICY));
      if (e.description) what.push(translate(MSG_EV_WHAT_DESCRIPTION));
      return compose(TPL_EV_SETTINGS, { what: what.join(', '), actor });
    }
    case 'joined':
      return e.byName ? compose(TPL_EV_JOINED_BY, { actor, by: e.byName }) : compose(TPL_EV_JOINED, { actor });
    case 'left':
      return zones ? compose(TPL_EV_LEFT_ZONES, { actor, zones }) : compose(TPL_EV_LEFT, { actor });
    case 'kicked':
      return compose(TPL_EV_KICKED, { target, actor });
    case 'role':
      return compose(e.role === 'officer' ? TPL_EV_OFFICER : TPL_EV_MEMBER, { target, actor });
    case 'leader':
      return compose(TPL_EV_LEADER, { actor, target });
    case 'leader_auto':
      return compose(e.reason === 'moderated' ? TPL_EV_LEADER_MODERATED : TPL_EV_LEADER_AUTO, { target });
    case 'zone_captured':
      return compose(TPL_EV_ZONE_CAPTURED, { zone: e.zone ?? 0, actor });
    case 'zone_lost':
      return e.byClanName ? compose(TPL_EV_ZONE_LOST_CLAN, { zone: e.zone ?? 0, by: e.byName ?? '?', clan: e.byClanName }) : compose(TPL_EV_ZONE_LOST, { zone: e.zone ?? 0, by: e.byName ?? '?' });
    case 'member_deleted':
      return compose(TPL_EV_MEMBER_DELETED, { target });
    case 'moderated':
      if (e.action === 'rename') return compose(TPL_EV_MOD_RENAME, { from: e.from ?? '', to: e.to ?? '' });
      if (e.action === 'description') return compose(MSG_EV_MOD_DESCRIPTION);
      if (e.action === 'leader') return compose(TPL_EV_MOD_LEADER, { target });
      return compose(TPL_EV_MOD_REMOVE, { target });
    default:
      return compose(e.type);
  }
}
